/**
 * Read a CSS media query from React.
 *
 * Used where a layout difference is structural rather than cosmetic: the
 * settings live inline on a wide screen and in a bottom sheet on a narrow one,
 * and rendering both would put two controls for every setting in the document.
 */

import { useCallback, useSyncExternalStore } from 'react'

/**
 * The one-screenful breakpoint, matching the one in `styles.css`.
 *
 * Two conditions, because a phone is short as often as it is narrow: turned
 * upright it runs out of width, turned on its side it runs out of height, and
 * in both cases the meters need every row the settings would otherwise take.
 */
export const COMPACT_QUERY = '(max-width: 34rem), (max-height: 30rem)'

const hasMatchMedia = (): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'

/**
 * Whether a media query currently matches, kept in step with the viewport.
 *
 * Subscribed rather than mirrored into state: the browser owns this value, and
 * `useSyncExternalStore` reads it at render time, so a rotation between render
 * and effect cannot leave the tree showing the wrong layout.
 *
 * @param query - A CSS media query.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!hasMatchMedia()) return () => undefined
      const list = window.matchMedia(query)
      list.addEventListener('change', onStoreChange)
      return () => {
        list.removeEventListener('change', onStoreChange)
      }
    },
    [query],
  )

  const getSnapshot = useCallback(
    () => (hasMatchMedia() ? window.matchMedia(query).matches : false),
    [query],
  )

  // Server-rendered output has no viewport; the wide layout is the safe guess.
  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}
